import React, { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, ArrowRight, LoaderCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { PublicPageShell } from '../../components/layout/PublicPageShell';
import { PageSeo } from '../../components/seo/PageSeo';
import { RismanServicesSection } from '../home/RismanServicesSection';
import { PublicTemplatesSection } from '../publicProjects/PublicTemplatesSection';
import { LandingSeoContent } from './LandingSeoContent';
import { useI18n } from '../../hooks/useI18n';
import { clearPendingPrompt, getPendingPrompt, savePendingPrompt } from '../../utils/storage';

const createDraftName = (prompt, language) => {
  const trimmed = prompt.trim();
  if (!trimmed) {
    return language === 'fa' ? 'پروژه جدید AI' : 'New AI Project';
  }

  const sanitized = trimmed.replace(/["'،,.!؟]/g, ' ').replace(/\s+/g, ' ').trim();
  const preview = sanitized.split(' ').slice(0, 5).join(' ');
  const suffix = sanitized.split(' ').length > 5 ? '...' : '';
  return preview ? `${preview}${suffix}` : language === 'fa' ? 'پروژه جدید AI' : 'New AI Project';
};

const fadeUp = {
  hidden: { opacity: 0, y: 14 },
  visible: { opacity: 1, y: 0 }
};

export const HeroPromptSection = () => {
  const navigate = useNavigate();
  const { t, language } = useI18n();
  const [prompt, setPrompt] = useState(() => getPendingPrompt() || '');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const faqItems = t('seoFaq') || [];

  const suggestions = t('landingSuggestions') || [];
  const placeholder = useMemo(() => t('landingPromptPlaceholder'), [t]);
  const DirectionArrow = language === 'fa' ? ArrowLeft : ArrowRight;

  const submitPrompt = async (value) => {
    const normalized = value.trim();
    if (!normalized || isSubmitting) {
      return;
    }

    setIsSubmitting(true);
    savePendingPrompt(normalized);

    const projectDraft = {
      name: createDraftName(normalized, language),
      description: normalized,
      visibility: 'your'
    };

    window.setTimeout(() => {
      navigate('/login', {
        state: {
          redirectTo: '/projects',
          openCreateProject: true,
          projectDraft
        }
      });
      setIsSubmitting(false);
    }, 500);
  };

  return (
    <PublicPageShell activePath="/">
      <PageSeo page="home" language={language} faqItems={faqItems} />

      <section aria-labelledby="landing-hero-title" className="pb-8 pt-12 sm:pb-10 sm:pt-16 md:pt-20">
        <motion.div
          initial="hidden"
          animate="visible"
          transition={{ staggerChildren: 0.06 }}
          className="w-full"
        >
          <div className="w-full text-center">
            <motion.h1
              id="landing-hero-title"
              variants={fadeUp}
              transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
              className="mx-auto max-w-3xl text-2xl font-bold leading-tight tracking-tight text-text sm:text-4xl"
            >
              {t('landingHeadline')}
            </motion.h1>

            <motion.form
              variants={fadeUp}
              transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
              className="mx-auto mt-8 max-w-2xl text-start sm:mt-10"
              onSubmit={(event) => {
                event.preventDefault();
                submitPrompt(prompt);
              }}
            >
              <label className="sr-only" htmlFor="landing-prompt">
                {t('landingPromptLabel')}
              </label>
              <div className="overflow-hidden rounded-2xl border border-border bg-panel shadow-sm focus-within:border-accent/45 focus-within:ring-2 focus-within:ring-accent/15">
                <textarea
                  id="landing-prompt"
                  value={prompt}
                  onChange={(event) => {
                    setPrompt(event.target.value);
                    if (!event.target.value.trim()) {
                      clearPendingPrompt();
                    }
                  }}
                  placeholder={placeholder}
                  rows={3}
                  className="block min-h-[6.5rem] w-full resize-none border-0 bg-transparent px-4 pb-2 pt-4 text-sm leading-7 text-text outline-none placeholder:text-muted/65 sm:min-h-[7rem] sm:text-base"
                />
                <div className="flex items-center justify-end px-3 pb-3">
                  <button
                    type="submit"
                    disabled={isSubmitting || !prompt.trim()}
                    aria-label={t('landingSubmit')}
                    className="inline-flex h-10 min-w-10 items-center justify-center gap-1.5 rounded-xl bg-accent px-3 text-white transition hover:opacity-95 disabled:cursor-not-allowed disabled:opacity-40 dark:text-slate-950"
                  >
                    {isSubmitting ? (
                      <LoaderCircle size={18} className="animate-spin" />
                    ) : (
                      <DirectionArrow size={18} strokeWidth={2.25} />
                    )}
                  </button>
                </div>
              </div>
            </motion.form>

            <motion.div
              variants={fadeUp}
              transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
              className="scrollbar-thin mx-auto mt-4 flex max-w-2xl snap-x snap-mandatory gap-2 overflow-x-auto pb-0.5 sm:mt-5"
            >
              {suggestions.map((suggestion) => (
                <button
                  key={suggestion}
                  type="button"
                  onClick={() => {
                    setPrompt(suggestion);
                    savePendingPrompt(suggestion);
                  }}
                  className="inline-flex shrink-0 snap-start whitespace-nowrap rounded-full border border-border/80 bg-panel/60 px-3 py-1.5 text-xs text-muted transition hover:border-accent/30 hover:text-text"
                >
                  {suggestion}
                </button>
              ))}
            </motion.div>
          </div>
        </motion.div>
      </section>

      <RismanServicesSection className="mt-10 sm:mt-12" />
      <PublicTemplatesSection variant="landing" />
      <LandingSeoContent />
    </PublicPageShell>
  );
};
